import React, { useCallback, useContext, useEffect, useState } from 'react';
import { UsuarioLoginInterface } from '../interfaces/UsuarioLoginInterface';

interface PlataformaInfo {
  id: number;
  nombre: string;
  slug: string;
  color_primario: string;
  color_secundario: string;
}

type AuthContextProps = {
  token: string | null;
  usuario: UsuarioLoginInterface | null;
  rol: string | null;
  permisos: string[];
  nivel: number;
  plataforma: PlataformaInfo | null;
  onAssetToken: string | null;
  onAssetAvailable: boolean;
  blacToken: string | null;
  blacAvailable: boolean;
  isLoggedIn: boolean;
  tienePermiso: (permiso: string) => boolean;
  login: (
    usuario: UsuarioLoginInterface | null,
    token: string | null,
    rol: string | null,
    plataforma?: PlataformaInfo | null,
    onAssetToken?: string | null,
    onAssetAvailable?: boolean,
    blacToken?: string | null,
    blacAvailable?: boolean,
    permisos?: string[],
    nivel?: number
  ) => void;
  logout: () => void;
};

export const AuthContext = React.createContext({} as AuthContextProps);

const retrieveStoredToken = () => {
  return localStorage.getItem('token');
};

const retrieveStoredRol = () => {
  return localStorage.getItem('rol');
};

const retrieveStoredUsuario = () => {
  const usuario = localStorage.getItem('usuario');
  return usuario ? JSON.parse(usuario) : null;
};

const retrieveStoredOnAssetToken = () => {
  return localStorage.getItem('onasset_token');
};

const retrieveStoredOnAssetAvailable = () => {
  const available = localStorage.getItem('onasset_available');
  return available === 'true';
};

const retrieveStoredBlacToken = () => {
  return localStorage.getItem('blac_token');
};

const retrieveStoredBlacAvailable = () => {
  const available = localStorage.getItem('blac_available');
  return available === 'true';
};

const retrieveStoredPlataforma = () => {
  const plataforma = localStorage.getItem('plataforma');
  return plataforma ? JSON.parse(plataforma) : null;
};

const retrieveStoredPermisos = (): string[] => {
  const permisos = localStorage.getItem('permisos');
  return permisos ? JSON.parse(permisos) : [];
};

const retrieveStoredNivel = (): number => {
  return parseInt(localStorage.getItem('nivel') || '0');
};

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [usuario, setUsuario] = useState<UsuarioLoginInterface | null>(null);
  const [rol, setRol] = useState<string | null>(null);
  const [plataforma, setPlataforma] = useState<PlataformaInfo | null>(null);
  const [onAssetToken, setOnAssetToken] = useState<string | null>(null);
  const [onAssetAvailable, setOnAssetAvailable] = useState<boolean>(false);
  const [blacToken, setBlacToken] = useState<string | null>(null);
  const [blacAvailable, setBlacAvailable] = useState<boolean>(false);
  const [permisos, setPermisos] = useState<string[]>([]);
  const [nivel, setNivel] = useState<number>(0);

  const tienePermiso = useCallback((permiso: string): boolean => {
    return permisos.includes(permiso);
  }, [permisos]);

  const logout = useCallback(() => {
    setToken(null);
    setUsuario(null);
    setRol(null);
    setPlataforma(null);
    setOnAssetToken(null);
    setOnAssetAvailable(false);
    setBlacToken(null);
    setBlacAvailable(false);
    setPermisos([]);
    setNivel(0);
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    localStorage.removeItem('rol');
    localStorage.removeItem('plataforma');
    localStorage.removeItem('onasset_token');
    localStorage.removeItem('onasset_available');
    localStorage.removeItem('blac_token');
    localStorage.removeItem('blac_available');
    localStorage.removeItem('permisos');
    localStorage.removeItem('nivel');
  }, []);

  const login = useCallback((
    usuario: UsuarioLoginInterface | null,
    token: string | null,
    rol: string | null,
    plataforma?: PlataformaInfo | null,
    onAssetToken?: string | null,
    onAssetAvailable?: boolean,
    blacToken?: string | null,
    blacAvailable?: boolean,
    permisos?: string[],
    nivel?: number
  ) => {
    setToken(token);
    setUsuario(usuario);
    setRol(rol);
    setPlataforma(plataforma || null);
    setOnAssetToken(onAssetToken || null);
    setOnAssetAvailable(onAssetAvailable || false);
    setBlacToken(blacToken || null);
    setBlacAvailable(blacAvailable || false);
    setPermisos(permisos || []);
    setNivel(nivel || 0);
    
    if (token) localStorage.setItem('token', token);
    if (usuario) localStorage.setItem('usuario', JSON.stringify(usuario));
    if (rol) localStorage.setItem('rol', rol);
    if (plataforma) localStorage.setItem('plataforma', JSON.stringify(plataforma));
    if (onAssetToken) localStorage.setItem('onasset_token', onAssetToken);
    localStorage.setItem('onasset_available', String(onAssetAvailable || false));
    if (blacToken) localStorage.setItem('blac_token', blacToken);
    localStorage.setItem('blac_available', String(blacAvailable || false));
    localStorage.setItem('permisos', JSON.stringify(permisos || []));
    localStorage.setItem('nivel', String(nivel || 0));
  }, []);

  useEffect(() => {
    const storedToken = retrieveStoredToken();
    const storedUsuario = retrieveStoredUsuario();
    const storedRol = retrieveStoredRol();
    const storedPlataforma = retrieveStoredPlataforma();
    const storedOnAssetToken = retrieveStoredOnAssetToken();
    const storedOnAssetAvailable = retrieveStoredOnAssetAvailable();
    const storedBlacToken = retrieveStoredBlacToken();
    const storedBlacAvailable = retrieveStoredBlacAvailable();

    if (storedToken && storedUsuario && storedRol) {
      setToken(storedToken);
      setUsuario(storedUsuario);
      setRol(storedRol);
      setPlataforma(storedPlataforma);
      setOnAssetToken(storedOnAssetToken);
      setOnAssetAvailable(storedOnAssetAvailable);
      setBlacToken(storedBlacToken);
      setBlacAvailable(storedBlacAvailable);
      setPermisos(retrieveStoredPermisos());
      setNivel(retrieveStoredNivel());
    }
  }, []);

  const isLoggedIn = !!token;

  const contextValue: AuthContextProps = {
    token,
    usuario,
    rol,
    permisos,
    nivel,
    plataforma,
    onAssetToken,
    onAssetAvailable,
    blacToken,
    blacAvailable,
    isLoggedIn,
    tienePermiso,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
